import { useState, useEffect, useCallback } from 'react';
import { get, put, post, del } from '../api';

function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const s = { primary:{background:'#c9a96e',color:'#1a1208',border:'none'}, secondary:{background:'transparent',color:'#8a7055',border:'1px solid #3a2a12'}, danger:{background:'#e63c3c22',color:'#e63c3c',border:'1px solid #e63c3c44'}, success:{background:'#10b98122',color:'#10b981',border:'1px solid #10b98144'}, ghost:{background:'#c9a96e18',color:'#c9a96e',border:'1px solid #c9a96e44'} };
  return <button onClick={onClick} disabled={disabled} style={{...s[variant],borderRadius:8,padding:small?'6px 14px':'10px 22px',fontWeight:700,cursor:disabled?'default':'pointer',fontSize:small?12:14,opacity:disabled?0.5:1,fontFamily:'inherit',transition:'all 0.15s',...style}}>{children}</button>;
}

function Modal({ children, onClose }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{background:'#1a1208',border:'1px solid #3a2a12',borderRadius:14,padding:32,maxWidth:700,width:'100%',maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>{children}</div>
    </div>
  );
}

const catColor = { Foundation:'#c9a96e', Operations:'#f59e0b', Activity:'#e63c3c', Safety:'#10b981', Systems:'#6366f1', Bar:'#a855f7', Kitchen:'#f97316', Events:'#06b6d4', Custom:'#06b6d4' };

export function QuickRefEditor({ userRole }) {
  const [modules, setModules] = useState([]);
  const [customCards, setCustomCards] = useState([]);
  const [editingModule, setEditingModule] = useState(null);
  const [editingCustom, setEditingCustom] = useState(null);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [quickRefText, setQuickRefText] = useState('');
  const [quickRefVisible, setQuickRefVisible] = useState(true);
  const [customForm, setCustomForm] = useState({ title:'', category:'General', content:'' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const mods = await get('/modules');
      setModules(mods.filter(m => m.status === 'published' || userRole === 'owner'));
      // Load custom quick ref cards
      try {
        const cards = await get('/quickref/custom');
        setCustomCards(cards);
      } catch(e) { setCustomCards([]); }
    } catch(e) {}
  }, [userRole]);

  useEffect(() => { load(); }, [load]);

  async function saveModuleQuickRef() {
    setSaving(true);
    setMsg('');
    try {
      await put(`/modules/${editingModule.id}/quickref`, { quick_ref: quickRefText, quick_ref_visible: quickRefVisible ? 1 : 0 });
      setMsg('✓ Saved!');
      setTimeout(() => { setEditingModule(null); setMsg(''); load(); }, 1000);
    } catch(e) { setMsg('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function saveCustomCard() {
    setSaving(true);
    setMsg('');
    try {
      if (editingCustom?.id) {
        await put(`/quickref/custom/${editingCustom.id}`, customForm);
      } else {
        await post('/quickref/custom', customForm);
      }
      setMsg('✓ Saved!');
      setTimeout(() => { setEditingCustom(null); setShowAddCustom(false); setMsg(''); setCustomForm({ title:'', category:'General', content:'' }); load(); }, 1000);
    } catch(e) { setMsg('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function deleteCustomCard(id) {
    if (!window.confirm('Delete this quick reference card?')) return;
    try { await del(`/quickref/custom/${id}`); load(); } catch(e) { alert(e.message); }
  }

  const isOwner = userRole === 'owner';

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h2 style={{fontFamily:"'Playfair Display', serif",fontSize:26,color:'#f5e6c8',marginBottom:4}}>Quick Reference Editor</h2>
          <p style={{color:'#8a7055',fontSize:13}}>Edit cheat sheets that staff can access during their shifts</p>
        </div>
        <Btn onClick={()=>{setShowAddCustom(true);setCustomForm({title:'',category:'General',content:''});}}>+ Add Card</Btn>
      </div>

      {/* Edit Module Quick Ref Modal */}
      {editingModule && (
        <Modal onClose={() => setEditingModule(null)}>
          <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:6}}>{editingModule.title}</h3>
          <p style={{color:'#8a7055',fontSize:13,marginBottom:16}}>Write the quick reference content for this module. Use plain text — staff will see it exactly as you type it.</p>
          <div style={{marginBottom:6}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Quick Reference Content</label>
            <textarea
              value={quickRefText}
              onChange={e => setQuickRefText(e.target.value)}
              rows={16}
              placeholder={"e.g.\nAGE RULES\n21+ after 8:30 PM\nAxe: 16+\nKrazy Darts: 12+\n\nGREETING SCRIPT\nHi, welcome to Tee & Timber!..."}
              style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'12px 14px',color:'#f5e6c8',fontSize:13,boxSizing:'border-box',fontFamily:'monospace',resize:'vertical',lineHeight:1.6}}
            />
          </div>
          <p style={{color:'#5a4030',fontSize:11,marginBottom:16}}>Tip: Use ALL CAPS for section headings, ✓ for checkboxes, → for arrows. Keep it short — this is a quick reference, not a full module.</p>
          <div onClick={()=>setQuickRefVisible(v=>!v)} style={{display:'flex',alignItems:'center',gap:12,background:'#2a1f0f',border:`1px solid ${quickRefVisible?'#10b98144':'#3a2a12'}`,borderRadius:10,padding:'12px 16px',marginBottom:16,cursor:'pointer'}}>
            <div style={{width:44,height:24,borderRadius:12,background:quickRefVisible?'#10b981':'#3a2a12',position:'relative',transition:'background 0.15s',flexShrink:0}}>
              <div style={{width:18,height:18,borderRadius:'50%',background:'#fff',position:'absolute',top:3,left:quickRefVisible?23:3,transition:'left 0.15s'}} />
            </div>
            <div>
              <div style={{color:'#f5e6c8',fontSize:13,fontWeight:600}}>Show on Quick Reference landing page</div>
              <div style={{color:'#8a7055',fontSize:12}}>{quickRefVisible?'Staff will see this card':'Hidden — content saved but not shown to staff'}</div>
            </div>
          </div>
          <p style={{color:'#5a4030',fontSize:11,marginBottom:16}}>Note: the card only appears to staff if the module is also Published in the Content Editor.</p>
          {msg && <p style={{color:msg.startsWith('✓')?'#10b981':'#e63c3c',fontSize:13,marginBottom:12}}>{msg}</p>}
          {!isOwner && <p style={{color:'#f59e0b',fontSize:12,marginBottom:12}}>⚠️ As GM, this will be submitted for Kevin's approval.</p>}
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={saveModuleQuickRef} disabled={saving}>{saving?'Saving...':'Save Quick Reference'}</Btn>
            <Btn variant="secondary" onClick={()=>setEditingModule(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Add/Edit Custom Card Modal */}
      {(showAddCustom || editingCustom) && (
        <Modal onClose={()=>{setShowAddCustom(false);setEditingCustom(null);}}>
          <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:6}}>{editingCustom?'Edit Card':'Add Quick Reference Card'}</h3>
          <p style={{color:'#8a7055',fontSize:13,marginBottom:16}}>Create a standalone quick reference card not tied to a training module.</p>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Card Title</label>
            <input value={customForm.title} onChange={e=>setCustomForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Bar Closing Checklist" style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit'}} />
          </div>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Category</label>
            <select value={customForm.category} onChange={e=>setCustomForm(p=>({...p,category:e.target.value}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,fontFamily:'inherit'}}>
              {['General','Safety','Operations','Bar','Kitchen','Activity','Foundation','Systems'].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Content</label>
            <textarea value={customForm.content} onChange={e=>setCustomForm(p=>({...p,content:e.target.value}))} rows={14} placeholder={"e.g.\nBAR CLOSING STEPS\n✓ Count till and record\n✓ Store all opened bottles\n✓ Clean and sanitize bar top\n✓ Empty ice bins\n✓ Restock for next shift"} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'12px 14px',color:'#f5e6c8',fontSize:13,boxSizing:'border-box',fontFamily:'monospace',resize:'vertical',lineHeight:1.6}} />
          </div>
          {msg && <p style={{color:msg.startsWith('✓')?'#10b981':'#e63c3c',fontSize:13,marginBottom:12}}>{msg}</p>}
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={saveCustomCard} disabled={saving||!customForm.title||!customForm.content}>{saving?'Saving...':'Save Card'}</Btn>
            <Btn variant="secondary" onClick={()=>{setShowAddCustom(false);setEditingCustom(null);}}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Module Quick Ref Cards */}
      <h3 style={{fontFamily:"'Playfair Display', serif",color:'#c9a96e',marginBottom:16}}>Module Quick Reference Cards</h3>
      <p style={{color:'#8a7055',fontSize:13,marginBottom:16}}>Each training module can have a quick reference card. Click Edit to add or update content.</p>
      <div style={{display:'grid',gap:10,marginBottom:32}}>
        {modules.map(m => {
          const cc = catColor[m.category] || '#c9a96e';
          const hasContent = m.quick_ref && m.quick_ref.trim().length > 0;
          return (
            <div key={m.id} style={{background:'#1a1208',border:`1px solid ${hasContent?cc+'44':'#3a2a12'}`,borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:8,height:8,borderRadius:'50%',background:hasContent?cc:'#3a2a12',flexShrink:0}} />
              <div style={{flex:1}}>
                <div style={{color:'#f5e6c8',fontWeight:600,fontSize:13}}>{m.title}</div>
                <div style={{color:'#5a4030',fontSize:12,marginTop:2}}>
                  {hasContent ? `${m.quick_ref.trim().split('\n').length} lines of content` : 'No quick reference card yet'}
                </div>
              </div>
              <span style={{background:cc+'22',color:cc,border:`1px solid ${cc}44`,borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700,textTransform:'uppercase'}}>{m.category}</span>
              <Btn small variant="ghost" onClick={()=>{setEditingModule(m);setQuickRefText(m.quick_ref||'');setQuickRefVisible(m.quick_ref_visible!==0);}}>
                {hasContent?'✏️ Edit':'+ Add'}
              </Btn>
            </div>
          );
        })}
      </div>

      {/* Custom Cards */}
      <h3 style={{fontFamily:"'Playfair Display', serif",color:'#c9a96e',marginBottom:16}}>Custom Quick Reference Cards</h3>
      <p style={{color:'#8a7055',fontSize:13,marginBottom:16}}>Standalone cards not tied to a module — great for bar procedures, cocktail specs, emergency contacts, etc.</p>
      {customCards.length === 0 && (
        <div style={{background:'#1a1208',border:'1px dashed #3a2a12',borderRadius:10,padding:24,textAlign:'center',marginBottom:16}}>
          <p style={{color:'#5a4030',fontSize:13}}>No custom cards yet. Click + Add Card to create one.</p>
        </div>
      )}
      <div style={{display:'grid',gap:10}}>
        {customCards.map(c => {
          const cc = catColor[c.category] || '#c9a96e';
          return (
            <div key={c.id} style={{background:'#1a1208',border:`1px solid ${cc}44`,borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:8,height:8,borderRadius:'50%',background:cc,flexShrink:0}} />
              <div style={{flex:1}}>
                <div style={{color:'#f5e6c8',fontWeight:600,fontSize:13}}>{c.title}</div>
                <div style={{color:'#5a4030',fontSize:12,marginTop:2}}>{c.content.trim().split('\n').length} lines · {c.category}</div>
              </div>
              <Btn small variant="ghost" onClick={()=>{setEditingCustom(c);setCustomForm({title:c.title,category:c.category,content:c.content});}}>✏️ Edit</Btn>
              {isOwner && <Btn small variant="danger" onClick={()=>deleteCustomCard(c.id)}>Delete</Btn>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
